import { Icon } from '@susu/ui/components'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  cancelOrderLines,
  getOrderDetails,
  getProductImageFromDetails,
} from '../../../api/ordersApi'
import type { Environment } from '../../../App'
import { useSkeletonPhase } from '../../../hooks/useSkeletonPhase'
import { useDateLocale, useLabel } from '../../../i18n'
import { backfillItemCharges } from '../../../utils/charges'
import { formatOrderDate } from '../../../utils/orderDate'
import type {
  Order as OrderDetailsType,
  OrderStateCard,
  OrderStateCta,
  TimelineLabels,
} from '../types'
import OrderDetailsSkeleton from './OrderDetailsSkeleton'
import OrdersTabView from './OrdersTabView'

interface OrderDetailsViewProps {
  orderId: string
  accountNumber: string
  env: Environment
  /**
   * All `active_orders` cards for the customer. Filtered here to the order
   * being viewed — `orderId` is a foreign key, so several cards can match.
   */
  orderStateCards?: OrderStateCard[]
  onOrderStateClick?: (card: OrderStateCard, cta: OrderStateCta) => void
  /** Translated timeline captions, supplied by the host. */
  timelineLabels?: TimelineLabels
  onBack: () => void
  role?: 'customer' | 'style-advisor'
  isCancellationFlowEnabled?: boolean
  authToken?: string
  onFaqOpen?: (title: string) => void
  onReturnClicked?: (orderId: string) => void
  onItemDetailsClick?: (item: OrderDetailsType['items'][number]) => void
  onReturnDetailsClick?: (item: OrderDetailsType['items'][number]) => void
  onCancellationClicked?: () => void
  onCancellationConfirmed?: () => void
  onCopyProductCode?: (code: string) => void
}

export default function OrderDetailsView({
  orderId,
  accountNumber,
  env,
  orderStateCards,
  onOrderStateClick,
  timelineLabels,
  onBack,
  role = 'customer',
  isCancellationFlowEnabled = true,
  authToken,
  onFaqOpen,
  onReturnClicked,
  onItemDetailsClick,
  onReturnDetailsClick,
  onCancellationClicked,
  onCancellationConfirmed,
  onCopyProductCode,
}: OrderDetailsViewProps) {
  const t = useLabel()
  const dateLocale = useDateLocale()
  const [orderHistory, setOrderHistory] = useState<string[]>([orderId])
  const scrollRef = useRef<HTMLDivElement>(null)
  const currentOrderId = orderHistory[orderHistory.length - 1]
  // The order the customer navigated here from. Only set while viewing a
  // return order, and only used to recover charges the return payload omits.
  const sourceOrderId =
    orderHistory.length > 1 ? orderHistory[orderHistory.length - 2] : undefined
  const cardsForCurrentOrder = (orderStateCards ?? []).filter(
    (card) => card.orderId === currentOrderId,
  )

  useEffect(() => {
    setOrderHistory([orderId])
  }, [orderId])

  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
    window.scrollTo({ top: 0 })
  }, [currentOrderId])

  const handleReturnDetailsClick = (
    item: OrderDetailsType['items'][number],
  ) => {
    if (item.returnOrderId && item.returnOrderId !== currentOrderId) {
      setOrderHistory((prev) => [...prev, item.returnOrderId!])
    }
    onReturnDetailsClick?.(item)
  }

  const handleBack = () => {
    if (orderHistory.length > 1) {
      setOrderHistory((prev) => prev.slice(0, -1))
      return
    }
    onBack()
  }

  const handleCancelContinue = async (
    items: OrderDetailsType['items'][number][],
  ) => {
    const orderLineIds = items.map((item) => String(item.orderLine))
    await cancelOrderLines(
      currentOrderId,
      orderLineIds,
      accountNumber,
      env,
      authToken,
    )
    onCancellationConfirmed?.()
    await refetch()
  }

  const {
    data: orderData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['orderDetails', currentOrderId, accountNumber, env],
    queryFn: () =>
      getOrderDetails(currentOrderId, accountNumber, env, authToken),
    enabled: !!currentOrderId && !!accountNumber,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })
  const { phase, contentClassName } = useSkeletonPhase(isLoading)

  // Already fetched and cached under the same key while the customer was on
  // that order, so this resolves without a request.
  const { data: sourceOrderData } = useQuery({
    queryKey: ['orderDetails', sourceOrderId, accountNumber, env],
    queryFn: () =>
      getOrderDetails(sourceOrderId!, accountNumber, env, authToken),
    enabled: !!sourceOrderId && !!accountNumber,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  // Exact-layout skeleton — breadcrumbs, header, item rows and totals — in
  // the view's own scroll wrapper (invisible for the first 150ms, so a
  // fast/cached response never flashes it).
  if (phase !== 'content') {
    return (
      <div
        className={`w-full max-lg:fixed inset-0 max-lg:z-50 bg-white overflow-y-auto${
          phase === 'reserve' ? ' invisible' : ''
        }`}
      >
        <OrderDetailsSkeleton />
      </div>
    )
  }

  if (isError || !orderData) {
    return (
      <div className="flex w-full max-md:max-w-[calc(100%-32px)] max-md:mt-24 max-lg:mx-auto md:max-lg:mt-40 md:max-lg:max-w-[490px] xl:max-w-5/8 3xl:max-w-4/8 flex-col gap-6 bg-surface-warning py-12 px-16">
        <button
          type="button"
          onClick={handleBack}
          className="self-start text-sm font-medium text-text underline"
          aria-label={t('orderList.navigation.goBackFromOrderDetails')}
        >
          {t('orderList.navigation.back')}
        </button>
        <div className="flex items-center gap-8">
          <Icon name="warning" size="medium" />
          <span className="text-sm font-medium text-text">
            {t('orderList.error.unavailable')}
          </span>
        </div>
        <span className="text-sm text-text">
          {t('orderList.error.tryAgain')}
        </span>
      </div>
    )
  }

  // RP-5940: a return order's totals include the alteration charge, but its
  // line items do not always carry the `charges` array behind it.
  const items = backfillItemCharges(orderData.items, sourceOrderData?.items)

  // Transform API order data to OrderDetails format
  const transformedOrder: OrderDetailsType = {
    id: orderData.orderId,
    orderNumber: orderData.orderId,
    orderDate: formatOrderDate(orderData.orderDate, dateLocale),
    orderedAt: orderData.location.name,
    location: orderData.location,
    env,
    canReturn: orderData.canReturn,
    items: items.map((item, index) => {
      const isCustomMade =
        item.customMadeDetails !== null || item.isCustomMadeLite

      return {
        // Derived UI fields
        id: `item-${item.legacyItemId || index}`,
        image: isCustomMade
          ? item.customMadeDetails?.imageUrl
          : getProductImageFromDetails(
              item.productDetails?.images,
              180,
              item.classCode,
            ),
        size: isCustomMade
          ? (item.customMadeDetails?.size ?? undefined)
          : (item.productDetails?.saleLocationSize ?? undefined),
        currency: orderData.currencyCode,
        isCustomMade,
        countryCode:
          orderData.shippingAddress?.countryCode ||
          orderData.billingAddress?.countryCode ||
          'NL',
        // Direct API fields
        productCode: item.productCode,
        classCode: item.classCode,
        name: item.name,
        productDetails: item.productDetails,
        customMadeDetails: item.customMadeDetails,
        price: item.price,
        discount: item.discount,
        subTotal: item.subTotal,
        quantity: item.quantity,
        total: item.total,
        valueAddedTax: item.valueAddedTax,
        tax: item.tax,
        orderLine: item.orderLine,
        canReturn: item.canReturn,
        legacyItemId: item.legacyItemId,
        giftCardNumber: item.giftCardNumber,
        isAlteration: item.isAlteration,
        alterationId: item.alterationId,
        charges: item.charges,
        trackingInfo: item.trackingInfo,
        promisedDeliveryDate: item.promisedDeliveryDate,
        maxFulfillmentStatusId: item.maxFulfillmentStatusId,
        minFulfillmentStatusId: item.minFulfillmentStatusId,
        returnLabelDocId: item.returnLabelDocId,
        returnOrderId: item.returnOrderId,
        isCustomMadeLite: item.isCustomMadeLite,
        isCancellable: item.isCancellable,
      }
    }),
    subtotal:
      orderData.totalAmount -
      orderData.valueAddedTax -
      orderData.saleTax -
      (orderData.shippingChargeAmount ?? 0),
    salesTax: orderData.valueAddedTax + orderData.saleTax,
    shipping: 'free', // TODO: Calculate shipping cost if available
    shippingChargeAmount: orderData.shippingChargeAmount ?? 0,
    total: orderData.totalAmount,
    currency: orderData.currencyCode,
    shippingAddress: orderData.shippingAddress
      ? {
          name: `${orderData.shippingAddress.firstName} ${orderData.shippingAddress.lastName}`,
          street: `${orderData.shippingAddress.addressLine1}${orderData.shippingAddress.addressLine2 ? ` ${orderData.shippingAddress.addressLine2}` : ''}`,
          city: orderData.shippingAddress.city,
          postalCode: orderData.shippingAddress.postalCode,
          country: orderData.shippingAddress.country,
          phone: orderData.shippingAddress.phone,
        }
      : null,
    billingAddress: orderData.billingAddress
      ? {
          name: `${orderData.billingAddress.firstName} ${orderData.billingAddress.lastName}`,
          street: `${orderData.billingAddress.addressLine1}${orderData.billingAddress.addressLine2 ? ` ${orderData.billingAddress.addressLine2}` : ''}`,
          city: orderData.billingAddress.city,
          postalCode: orderData.billingAddress.postalCode,
          country: orderData.billingAddress.country,
          phone: orderData.billingAddress.phone,
        }
      : null,
  }

  return (
    <div
      ref={scrollRef}
      className={`w-full max-lg:fixed inset-0 max-lg:z-50 bg-white overflow-y-auto${
        contentClassName ? ` ${contentClassName}` : ''
      }`}
    >
      <OrdersTabView
        order={transformedOrder}
        env={env}
        orderStateCards={cardsForCurrentOrder}
        onOrderStateClick={onOrderStateClick}
        timelineLabels={timelineLabels}
        isReturnDetails={orderHistory.length > 1}
        onBack={handleBack}
        role={role}
        isCancellationFlowEnabled={isCancellationFlowEnabled}
        onFaqOpen={onFaqOpen}
        onReturnClicked={onReturnClicked}
        onItemDetailsClick={onItemDetailsClick}
        onReturnDetailsClick={handleReturnDetailsClick}
        onCancelContinue={handleCancelContinue}
        onCancellationClicked={onCancellationClicked}
        onCopyProductCode={onCopyProductCode}
      />
    </div>
  )
}
